/**
 * Rank badge — the showbiz dressing for a raw level number. Resolves a level into
 * its POPSTAR rank + tier (via the pure {@link rankForLevel}) and renders a compact
 * glass chip: the single-word rank name with the tier number, tinted by tier so the
 * 16-tier climb reads at a glance. Presentational only — give it a level.
 *
 * Variants:
 *   - `chip` (default): a small inline pill (rank name + tier roman) — header cards.
 *   - `full`: a stacked block (rank name, "Tier N · Tier Name", level) — dashboards.
 *
 * Tier colour is exposed as the `--rank-accent` CSS var (consumed in index.css)
 * so a Diamond rank glints differently from a Spark — derived from the tier index
 * only, no per-rank art needed.
 */
import type { CSSProperties } from 'react';
import { rankForLevel } from '../core/ranks';

interface RankBadgeProps {
  /** The account level (from `levelForXp(xp).level`). Clamped to a valid rank. */
  level: number;
  /** `chip` (inline pill) or `full` (stacked block). Default `chip`. */
  variant?: 'chip' | 'full';
  /** Show the numeric level alongside the rank (chip variant). Default true. */
  showLevel?: boolean;
  className?: string;
}

/**
 * A per-tier accent hue (HSL hue in degrees), sweeping the spectrum across the 16
 * tiers so each tier reads distinctly — Spark (warm) → Eternal (royal). Lives here
 * (not the rank data) because it's purely cosmetic.
 */
function tierHue(tier: number): number {
  // 16 tiers across ~300° of hue, offset so tier 1 starts amber.
  return (40 + (tier - 1) * 20) % 360;
}

export function RankBadge({ level, variant = 'chip', showLevel = true, className }: RankBadgeProps) {
  const rank = rankForLevel(level);
  const hue = tierHue(rank.tier);
  const style = { '--rank-accent': `${hue}` } as CSSProperties;

  if (variant === 'full') {
    return (
      <div className={'rank-badge rank-badge-full' + (className ? ` ${className}` : '')} style={style}>
        <span className="rank-badge-name">{rank.name}</span>
        <span className="rank-badge-tier">
          {rank.tierRoman} · {rank.tierName}
        </span>
        <span className="rank-badge-level">Level {rank.level}</span>
      </div>
    );
  }

  return (
    <span
      className={'rank-badge rank-badge-chip' + (className ? ` ${className}` : '')}
      style={style}
      title={`${rank.name} — Tier ${rank.tier} (${rank.tierName})`}
    >
      <span className="rank-badge-dot" aria-hidden />
      <span className="rank-badge-name">{rank.name}</span>
      {showLevel && <span className="rank-badge-lv">Lv {rank.level}</span>}
    </span>
  );
}
